import { useState, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Upload, FileSpreadsheet, AlertTriangle, Loader2, CheckCircle2, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Dialog d'import Excel de lignes catalogue (panier fournisseur).
 *
 * Mutualisé entre OR et Devis : passer `entityType="or"` + `entityId=orId`
 * (ou `entityType="devis"` + `entityId=did`). Les URLs cibles sont dérivées
 * automatiquement — single dialog, même UX, même endpoints.
 *
 * Format Excel attendu : .xlsx avec colonnes au minimum :
 *  - Description de l'article · Nombre · Prix cons.
 */
export default function ExcelImportDialog({
  open,
  onClose,
  orId,                  // (legacy) raccourci pour entityType="or"
  entityType = "or",     // "or" | "devis"
  entityId,
  onImported,
}) {
  // Compat ascendante : si `orId` est fourni mais pas `entityId`
  const eid = entityId ?? orId;
  const etype = orId ? "or" : entityType;
  const basePath = etype === "devis" ? `/devis/${eid}/lignes/import` : `/or/${eid}/lignes/import`;
  const targetLabel = etype === "devis" ? "devis" : "OR";

  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);  // { lignes, warnings, count }
  const [selected, setSelected] = useState({});  // {idx: bool}
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setSelected({});
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleFile = async (f) => {
    setFile(f);
    setParsing(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`${basePath}/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      // Tout coché par défaut
      const all = {};
      (data.lignes || []).forEach((_, i) => { all[i] = true; });
      setSelected(all);
    } catch (e) {
      const detail = e.response?.data?.detail || "Erreur de lecture du fichier";
      toast.error(detail, { duration: 6000 });
      setFile(null);
    } finally {
      setParsing(false);
    }
  };

  const toggleAll = (val) => {
    const next = {};
    (preview?.lignes || []).forEach((_, i) => { next[i] = val; });
    setSelected(next);
  };
  const countChecked = Object.values(selected).filter(Boolean).length;

  const commit = async () => {
    const lignes = (preview?.lignes || []).filter((_, i) => selected[i]);
    if (!lignes.length) {
      toast.error("Aucune ligne à importer");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`${basePath}/commit`, { lignes });
      toast.success(`${data.inserted} ligne(s) ajoutée(s) au ${targetLabel}`);
      onImported && onImported();
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-3xl" data-testid="excel-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#52a675]" /> Importer des lignes depuis un fichier Excel
          </DialogTitle>
          <DialogDescription>
            Le fichier doit contenir les colonnes <strong>Description de l'article</strong>, <strong>Nombre</strong> et <strong>Prix cons.</strong> Les autres colonnes (numéro d'article, total, etc.) sont ignorées.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="space-y-3">
            <label
              className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                parsing ? "border-[#52a675]/40 bg-[#f0f9f4]" : "border-gray-300 hover:border-[#52a675] hover:bg-[#f0f9f4]"
              }`}
              data-testid="excel-import-drop"
            >
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xlsm,.csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
                data-testid="excel-import-file"
              />
              {parsing ? (
                <div className="text-sm text-[#3d8a5c]"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Lecture du fichier…</div>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                  <div className="font-semibold text-gray-700">Cliquez pour sélectionner un fichier</div>
                  <div className="text-xs text-gray-500 mt-1">.xlsx, .xlsm ou .csv (max 5 Mo)</div>
                </>
              )}
            </label>
            <div className="text-[11px] text-gray-500 leading-relaxed">
              <strong>Mapping :</strong> Description → Libellé · Nombre → Quantité · Prix cons. → Prix unitaire HT. Les colonnes <em>Numéro d'article, Type de p. cons., Prix net par pièce, Total</em> sont ignorées.
            </div>
          </div>
        )}

        {preview && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm">
                <span className="font-semibold">{preview.count}</span> ligne(s) détectée(s) dans <code className="bg-gray-50 px-1.5 py-0.5 rounded text-xs">{file?.name}</code>
              </div>
              <div className="flex gap-1">
                <button onClick={() => toggleAll(true)} className="text-xs text-[#3d8a5c] hover:underline" data-testid="excel-select-all">Tout sélectionner</button>
                <span className="text-gray-300">·</span>
                <button onClick={() => toggleAll(false)} className="text-xs text-gray-500 hover:underline" data-testid="excel-select-none">Tout décocher</button>
              </div>
            </div>

            {preview.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-xs text-amber-900" data-testid="excel-warnings">
                <div className="flex items-center gap-1.5 font-semibold mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> {preview.warnings.length} avertissement(s)
                </div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            <div className="border border-gray-200 rounded-md max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 w-10"></th>
                    <th className="text-left p-2">Description</th>
                    <th className="text-right p-2 w-20">Qté</th>
                    <th className="text-right p-2 w-24">P.U. HT</th>
                    <th className="text-right p-2 w-24">Total HT</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lignes.map((l, i) => {
                    const total = (l.quantite || 0) * (l.prix_unitaire_ht || 0);
                    return (
                      <tr key={i} className={`border-t border-gray-100 ${selected[i] ? "" : "opacity-40 bg-gray-50/30"}`} data-testid={`excel-preview-row-${i}`}>
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={!!selected[i]}
                            onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                            className="w-4 h-4 accent-[#52a675]"
                            data-testid={`excel-preview-check-${i}`}
                          />
                        </td>
                        <td className="p-2">{l.libelle}</td>
                        <td className="p-2 text-right font-mono">{l.quantite}</td>
                        <td className="p-2 text-right font-mono">{l.prix_unitaire_ht?.toFixed(2)}</td>
                        <td className="p-2 text-right font-mono font-semibold">{total.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <button onClick={reset} className="text-xs text-gray-500 hover:underline inline-flex items-center gap-1" data-testid="excel-reset">
              <X className="w-3 h-3" /> Choisir un autre fichier
            </button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close}>Annuler</Button>
          {preview && (
            <Button
              onClick={commit}
              disabled={submitting || countChecked === 0}
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              data-testid="excel-import-commit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              Importer {countChecked} ligne(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
